import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import PassageInput, { parsePassageInputValue } from '../components/PassageInput';
import {
  formatPassageLabel,
  createPassagePlan,
  updatePassagePlan,
  fetchMyCreatedPassagePlans,
  fetchFamilyPassagePlans,
  fetchMyPassagePlanSubscriptions,
  fetchPassagePlan,
  fetchUserRecordingsForPassagePlan,
  computePassagePlanProgress,
  subscribePassagePlan,
  unsubscribePassagePlan,
  syncPassagePlanSubName,
} from '../utils/passagePlans';

function PassagesPlansPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);

  const [tab, setTab] = useState('mine'); // 'mine' | 'family'
  const [loading, setLoading] = useState(true);
  const [myPlans, setMyPlans] = useState([]);       // subscribed plans (full doc + sub data)
  const [familyPlans, setFamilyPlans] = useState([]); // family-visibility plans
  const [subscriptionIds, setSubscriptionIds] = useState(new Set());
  const [progressMap, setProgressMap] = useState({});
  const [busy, setBusy] = useState(null); // planId being acted on
  const [error, setError] = useState('');

  // Create / Edit form
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newVisibility, setNewVisibility] = useState('personal');
  const [newPassagesInput, setNewPassagesInput] = useState('');

  const uid = currentUser?.uid;
  const familyIds = currentUser?.familyIds || [];
  const familyId = familyIds[0];

  // Arriving from Listen's "+ Create a new plan" long-press — pre-fill the verse
  useEffect(() => {
    const prefill = searchParams.get('prefill');
    if (prefill) { setShowForm(true); setNewPassagesInput(`${prefill}, `); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!uid || !familyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [subs, famPlans, userRecs] = await Promise.all([
        fetchMyPassagePlanSubscriptions(uid),
        fetchFamilyPassagePlans(familyIds),
        fetchUserRecordingsForPassagePlan(uid),
      ]);

      // Fetch full plan docs for subscriptions
      const planDocs = await Promise.all(subs.map(s => fetchPassagePlan(s.planId)));
      const subIds = new Set(subs.map(s => s.planId));

      const merged = subs
        .map((s, i) => planDocs[i] ? { ...planDocs[i], ...s, id: s.planId } : null)
        .filter(Boolean)
        .sort((a, b) => (a.planName || a.name || '').localeCompare(b.planName || b.name || ''));

      const progMap = {};
      for (const plan of [...merged, ...famPlans]) {
        if (!progMap[plan.id]) {
          progMap[plan.id] = computePassagePlanProgress(plan.passages || [], userRecs);
        }
      }

      setMyPlans(merged);
      setFamilyPlans(famPlans);
      setSubscriptionIds(subIds);
      setProgressMap(progMap);
    } catch (err) {
      console.error('PassagePlans load error:', err);
      setError('Failed to load Passage Plans.');
    } finally {
      setLoading(false);
    }
  }, [uid, familyIds.join(',')]);

  useEffect(() => { load(); }, [load]);

  // ── Form helpers ──────────────────────────────────────────────────────────

  const resetForm = () => {
    setShowForm(false);
    setEditingPlan(null);
    setNewName('');
    setNewDescription('');
    setNewVisibility('personal');
    setNewPassagesInput('');
    setError('');
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setNewName(plan.name || '');
    setNewDescription(plan.description || '');
    setNewVisibility(plan.visibility || 'personal');
    const passageStr = (plan.passages || []).map(formatPassageLabel).join(', ');
    setNewPassagesInput(passageStr ? passageStr + ', ' : '');
    setShowForm(true);
    setError('');
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const handleSave = async () => {
    if (!newName.trim()) { setError('Give your plan a name.'); return; }
    const passages = parsePassageInputValue(newPassagesInput);
    if (passages.length === 0) { setError('Add at least one recognized passage (e.g., "John 3:16").'); return; }

    setSaving(true);
    setError('');
    try {
      if (editingPlan) {
        await updatePassagePlan(editingPlan.id, {
          name: newName.trim(),
          description: newDescription.trim(),
          passages,
          visibility: newVisibility,
        });
        await syncPassagePlanSubName(uid, editingPlan.id, newName.trim()).catch(() => {});
      } else {
        const createdByName =
          currentUser?.displayName ||
          currentUser?.firstName ||
          currentUser?.email || '';
        const ref = await createPassagePlan({
          name: newName.trim(),
          description: newDescription.trim(),
          passages,
          visibility: newVisibility,
          familyId,
          createdBy: uid,
          createdByName,
        });
        // Auto-subscribe so it appears in My Plans immediately.
        await subscribePassagePlan(uid, {
          id: ref.id,
          name: newName.trim(),
          passages,
          createdBy: uid,
          createdByName,
        });
      }
      resetForm();
      await load();
    } catch (err) {
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Subscribe actions ─────────────────────────────────────────────────────

  const handleCopyToMyPlans = async (plan) => {
    setBusy(plan.id);
    try {
      await subscribePassagePlan(uid, plan);
      await load();
      setTab('mine');
    } catch (err) {
      setError(`Failed to copy plan: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const handleRecord = async (plan) => {
    // Make this the active plan before navigating so usePassagePlanManagement picks it up.
    await updateDoc(doc(db, 'users', uid), { lastPassagePlanId: plan.id }).catch(() => {});
    navigate('/record');
  };

  const handleRemove = async (plan) => {
    if (!window.confirm(`Remove "${plan.planName || plan.name}" from My Plans? Your recordings won't be deleted.`)) return;
    setBusy(`unsub:${plan.id}`);
    try {
      await unsubscribePassagePlan(uid, plan.id);
      await load();
    } catch (err) {
      setError(`Failed to remove: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  // ── Progress bar ──────────────────────────────────────────────────────────

  const ProgressBar = ({ planId, passages }) => {
    const prog = progressMap[planId] ?? { completed: 0, total: passages?.length ?? 0, pct: 0 };
    if (!passages?.length) return null;
    return (
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-400 dark:text-white/40">
            {prog.completed} of {prog.total} passages recorded
          </span>
          <span className="text-xs font-semibold text-cerulean">{prog.pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-100 dark:bg-white/10 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-cerulean to-aquamarine transition-all"
            style={{ width: `${prog.pct}%` }}
          />
        </div>
      </div>
    );
  };

  // ── Plan cards ────────────────────────────────────────────────────────────

  const MyPlanCard = ({ plan }) => {
    const passages = plan.passages || [];
    const prog = progressMap[plan.id] ?? { completedIndexes: [], nextPassage: null };
    const doneSet = new Set(prog.completedIndexes || []);
    const isMine = plan.createdBy === uid;
    const authorLabel = !isMine && plan.authorName ? `by ${plan.authorName}` : null;
    const isRemoving = busy === `unsub:${plan.id}`;

    return (
      <div className="card">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 dark:text-white">
                {plan.planName || plan.name}
              </span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                plan.visibility === 'family'
                  ? 'bg-cerulean/10 text-cerulean'
                  : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-white/50'
              }`}>
                {plan.visibility === 'family' ? 'Family' : 'Personal'}
              </span>
            </div>
            {authorLabel && (
              <p className="text-xs text-gray-400 dark:text-white/40 mt-0.5">{authorLabel}</p>
            )}
            {plan.description && (
              <p className="text-sm text-gray-500 dark:text-white/60 mt-1">{plan.description}</p>
            )}
            <ProgressBar planId={plan.id} passages={passages} />
            <div className="flex flex-wrap gap-1.5 mt-3">
              {passages.map((p, i) => (
                <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${
                  doneSet.has(i)
                    ? 'bg-green-50 dark:bg-green-400/10 border-green-200 dark:border-green-400/25 text-green-700 dark:text-green-400'
                    : 'bg-cerulean/8 border-cerulean/15 text-cerulean'
                }`}>
                  {doneSet.has(i) && (
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {formatPassageLabel(p)}
                </span>
              ))}
            </div>
            {isMine && (
              <button
                onClick={() => openEdit(plan)}
                className="mt-3 text-xs text-cerulean hover:text-cerulean/80 font-medium flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit plan
              </button>
            )}
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => handleRecord(plan)}
            className="flex-1 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-cerulean to-aqua-deep text-white hover:opacity-90 transition-opacity"
          >
            {prog?.nextPassage ? `Record ${formatPassageLabel(prog.nextPassage)}` : 'Record'}
          </button>
          <button
            onClick={() => handleRemove(plan)}
            disabled={isRemoving}
            className="px-4 py-2 rounded-xl text-sm text-gray-400 dark:text-white/40 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
          >
            {isRemoving ? '…' : 'Remove'}
          </button>
        </div>
      </div>
    );
  };

  const FamilyPlanCard = ({ plan }) => {
    const passages = plan.passages || [];
    const isSubscribed = subscriptionIds.has(plan.id);
    const prog = progressMap[plan.id] ?? { completedIndexes: [], nextPassage: null };
    const doneSet = new Set(prog.completedIndexes || []);
    const isBusy = busy === plan.id;
    const authorLabel = plan.createdByName || (plan.createdBy === uid ? 'Me' : null);
    const isCopyable = plan.visibility === 'family' || plan.visibility === 'public';
    const visibilityBadge = plan.visibility === 'public' ? 'Public' : plan.visibility === 'family' ? 'Family' : 'Private';

    return (
      <div className={`card ${isSubscribed ? 'ring-1 ring-cerulean/30' : ''}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 dark:text-white">{plan.name}</span>
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-400">{visibilityBadge}</span>
            </div>
            {authorLabel && (
              <p className="text-xs text-gray-400 dark:text-white/40 mt-0.5">by {authorLabel}</p>
            )}
            {plan.description && (
              <p className="text-sm text-gray-500 dark:text-white/60 mt-1">{plan.description}</p>
            )}
            {isSubscribed && <ProgressBar planId={plan.id} passages={passages} />}
            <div className="flex flex-wrap gap-1.5 mt-3">
              {passages.map((p, i) => (
                <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${
                  isSubscribed && doneSet.has(i)
                    ? 'bg-green-50 dark:bg-green-400/10 border-green-200 dark:border-green-400/25 text-green-700 dark:text-green-400'
                    : 'bg-cerulean/8 border-cerulean/15 text-cerulean'
                }`}>
                  {isSubscribed && doneSet.has(i) && (
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {formatPassageLabel(p)}
                </span>
              ))}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-2xl font-bold text-cerulean">{passages.length}</p>
            <p className="text-xs text-gray-400 leading-none">passages</p>
          </div>
        </div>
        <div className="mt-4">
          {isSubscribed ? (
            <button
              onClick={() => handleRecord(plan)}
              className="w-full py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-cerulean to-aqua-deep text-white hover:opacity-90 transition-opacity"
            >
              {prog?.nextPassage ? `Record ${formatPassageLabel(prog.nextPassage)}` : 'Record'}
            </button>
          ) : isCopyable ? (
            <button
              onClick={() => handleCopyToMyPlans(plan)}
              disabled={isBusy}
              className="w-full py-2 rounded-xl text-sm font-semibold border border-cerulean/40 text-cerulean hover:bg-cerulean/5 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {isBusy ? '…' : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy to My Plans
                </>
              )}
            </button>
          ) : (
            <div className="w-full py-2 rounded-xl text-sm text-gray-400 bg-gray-50 dark:bg-white/5 flex items-center justify-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 18.5v.5m0-13v.5m-3.536.964a5 5 0 000 7.072M6.343 6.343A8 8 0 0117.657 17.657M6.343 6.343L3 3m3.343 3.343A8 8 0 0117.657 17.657M17.657 17.657L21 21" />
              </svg>
              Listen Only
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Please Sign In</h2>
          <button onClick={() => navigate('/auth')} className="btn-primary">Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 pt-6 pb-28">
      <button
        onClick={() => navigate('/profile')}
        className="text-cerulean hover:text-cerulean/80 text-sm font-medium mb-6 flex items-center gap-1"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Profile
      </button>

      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Passage Plans</h1>
          <p className="text-gray-500 dark:text-white/60 text-sm mt-1">
            Curated verse-by-verse plans — memorization sets, topical passages, or a sequence
            like the Romans Road.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-2xl bg-red-50 dark:bg-red-400/10 border border-red-200 dark:border-red-400/25 p-4 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Create / Edit form */}
      <button
        onClick={() => showForm ? resetForm() : setShowForm(true)}
        className="mb-4 w-full py-3 rounded-2xl border-2 border-dashed border-cerulean/30 text-cerulean font-semibold text-sm hover:bg-cerulean/5 transition-colors"
      >
        {showForm ? 'Cancel' : '+ Create a Passage Plan'}
      </button>

      {showForm && (
        <div ref={formRef} className="card mb-6 space-y-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {editingPlan ? `Edit "${editingPlan.name}"` : 'New Passage Plan'}
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-1">Plan Name</label>
            <input type="text" className="input-field" value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g., Romans Road, Verses for Peace" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-1">Description</label>
            <textarea className="input-field" rows={2} value={newDescription} onChange={e => setNewDescription(e.target.value)} placeholder="What's this plan for?" />
          </div>

          <PassageInput id="newPassages" label="Passages" value={newPassagesInput} onChange={setNewPassagesInput} />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">Who can record this plan?</label>
            <div className="flex gap-2">
              {[['personal', 'Private'], ['family', 'Family'], ['public', 'Public']].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setNewVisibility(v)}
                  className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    newVisibility === v ? 'bg-cerulean text-white' : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/60'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-gray-400 dark:text-white/40">
              {newVisibility === 'family'
                ? 'Family members can copy this plan and record their own version.'
                : newVisibility === 'public'
                ? 'Anyone can find and copy this plan to record their own version.'
                : 'Only you record this. Your family can still find and listen to it.'}
            </p>
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2.5 rounded-xl bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold text-sm disabled:opacity-50"
          >
            {saving ? 'Saving…' : editingPlan ? 'Save Changes' : 'Create Plan'}
          </button>
        </div>
      )}

      {/* Tab toggle */}
      <div className="flex rounded-xl bg-gray-100 dark:bg-white/10 p-1 mb-6">
        {[['mine', 'My Plans'], ['family', 'Family Plans']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
              tab === key
                ? 'bg-white dark:bg-white/15 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-white/50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cerulean" />
        </div>
      ) : (
        <>
          {tab === 'mine' && (
            <div className="space-y-3">
              {myPlans.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-gray-200 dark:border-white/10 p-10 text-center">
                  <p className="text-sm text-gray-400 dark:text-white/40">
                    No plans yet. Create one above, or copy a plan from the Family Plans tab.
                  </p>
                </div>
              ) : (
                myPlans.map(plan => <MyPlanCard key={plan.id} plan={plan} />)
              )}
            </div>
          )}

          {tab === 'family' && (
            <div className="space-y-3">
              {familyPlans.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-gray-200 dark:border-white/10 p-10 text-center">
                  <p className="text-sm text-gray-400 dark:text-white/40">
                    No family plans yet. Create a plan and set it to "Share with Family".
                  </p>
                </div>
              ) : (
                familyPlans.map(plan => <FamilyPlanCard key={plan.id} plan={plan} />)
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PassagesPlansPage;
